import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { CheckIcon, ChevronDownIcon, Loader2Icon, TriangleAlertIcon, XIcon } from "lucide-react";
import { APP_CHAT_RICH_PART_TYPES } from "@bliks-io/contracts";
import { cn } from "../../../utils";
import { useFileDrop } from "../../../hooks/useFileDrop";
import { AiThinkingIndicator } from "../ai-thinking-indicator";
import { Attachment, AttachmentContent, AttachmentGroup, AttachmentMedia, AttachmentTitle } from "../chat/attachment";
import { ChatAttachmentIcon } from "./ai-chat-composing-attachments";
import { AiChatDropHint } from "./ai-chat-drop-hint";
import { Bubble, BubbleContent } from "../chat/bubble";
import { Marker, MarkerContent, MarkerIcon } from "../chat/marker";
import { Message, MessageAvatar, MessageContent } from "../chat/message";
import { MessageScroller, MessageScrollerButton, MessageScrollerContent, MessageScrollerItem, MessageScrollerProvider, MessageScrollerViewport } from "../chat/message-scroller";
/**
 * Distance (px) from the bottom within which the transcript keeps following new
 * content — beyond it the reader is reading back and streaming must not yank
 * them down. Same threshold the platform transcripts use.
 */ const SCROLL_EDGE_THRESHOLD = 100;
const THINKING_INDICATOR_ID = "ai-chat-panel-thinking";
/**
 * The readable-measure cap shared by the transcript and the host's composer: a
 * full-page host keeps both on one column width, while inside a narrow panel it
 * never binds. Exported because the composer is the host's slot and has to
 * match the column it sits under.
 */ export const CHAT_COLUMN_MEASURE = "mx-auto w-full max-w-4xl";
const TOOL_STATE_ICON = {
    "input-streaming": /*#__PURE__*/ _jsx(Loader2Icon, {
        className: "animate-spin",
        "aria-hidden": true
    }),
    "input-available": /*#__PURE__*/ _jsx(Loader2Icon, {
        className: "animate-spin",
        "aria-hidden": true
    }),
    "output-available": /*#__PURE__*/ _jsx(CheckIcon, {
        "aria-hidden": true
    }),
    "output-error": /*#__PURE__*/ _jsx(TriangleAlertIcon, {
        "aria-hidden": true
    })
};
function isTextPart(part) {
    return part.type === "text";
}
function isFilePart(part) {
    return part.type === "file";
}
function isToolPart(part) {
    return part.type === "tool";
}
const RICH_PART_TYPES = new Set(APP_CHAT_RICH_PART_TYPES);
const EMPTY_TOOL_LABELS = {};
function isRichPart(part) {
    return RICH_PART_TYPES.has(part.type);
}
/**
 * Whether a part would actually put something on screen. A text part that is
 * still whitespace belongs to the thinking indicator; a file or a tool call
 * always draws a chip or a status row, in every state.
 *
 * The regex avoids `trim()`, which would copy a still-streaming string on every
 * token just to answer a yes/no question.
 */ function hasVisibleContent(part) {
    if (isTextPart(part)) return /\S/.test(part.text);
    return true;
}
function hasRenderablePart(message) {
    return message.parts.some(hasVisibleContent);
}
/**
 * The indicator covers the gap between sending and the first visible token: the
 * user's turn still unanswered, or an assistant turn that has started streaming
 * but rendered nothing yet.
 */ function awaitsFirstToken(messages) {
    const last = messages[messages.length - 1];
    if (!last) return true;
    return last.role === "user" || !hasRenderablePart(last);
}
function AiChatPanelToolActivity({ part, toolLabels, rejectedToolLabel, detail }) {
    const label = (part.rejected ? rejectedToolLabel : undefined) ?? toolLabels[part.toolName] ?? part.toolName;
    const isPlain = part.rejected || part.state === "output-error";
    return /*#__PURE__*/ _jsxs(Marker, {
        "data-slot": "ai-chat-tool-activity",
        "data-tool-name": part.toolName,
        "data-rejected": part.rejected ? "true" : undefined,
        children: [
            /*#__PURE__*/ _jsx(MarkerIcon, {
                variant: isPlain ? "default" : "ai",
                children: part.rejected ? /*#__PURE__*/ _jsx(XIcon, {
                    "aria-hidden": true
                }) : TOOL_STATE_ICON[part.state]
            }),
            /*#__PURE__*/ _jsxs(MarkerContent, {
                children: [
                    label,
                    detail != null && detail !== "" && /*#__PURE__*/ _jsxs("span", {
                        className: "text-muted-foreground/70",
                        children: [
                            " · ",
                            detail
                        ]
                    })
                ]
            })
        ]
    });
}
/** A tool call that has not settled yet — its group still reads as thinking. */ function isRunningToolPart(part) {
    return part.state === "input-streaming" || part.state === "input-available";
}
function toTurnSegments(parts) {
    const segments = [];
    parts.forEach((part, index)=>{
        const last = segments[segments.length - 1];
        if (isToolPart(part)) {
            if (last?.kind === "tools") last.runs.push({
                part,
                index
            });
            else segments.push({
                kind: "tools",
                runs: [
                    {
                        part,
                        index
                    }
                ]
            });
        } else {
            segments.push({
                kind: "part",
                part,
                index
            });
        }
    });
    return segments;
}
/**
 * A run of tool calls behind one expandable summary row. Collapsed it shows the
 * thinking indicator while any call still runs — the reader sees progress, not
 * plumbing — and the host's settled summary once all are done; expanding shows
 * the individual activity rows. A native `<details>`: the open state is the
 * DOM's, so a settled turn keeps whatever the reader chose.
 */ function AiChatPanelToolGroup({ runs, summaryLabel, thinkingPhases, toolLabels, rejectedToolLabel, renderToolDetail }) {
    const running = runs.some(({ part })=>isRunningToolPart(part));
    return /*#__PURE__*/ _jsxs("details", {
        "data-slot": "ai-chat-tool-group",
        className: "group/tools",
        children: [
            /*#__PURE__*/ _jsxs("summary", {
                "aria-label": summaryLabel(runs.length),
                className: "flex cursor-pointer list-none items-center gap-2 text-sm text-muted-foreground [&::-webkit-details-marker]:hidden",
                children: [
                    running ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(MarkerIcon, {
                                variant: "ai",
                                children: /*#__PURE__*/ _jsx(Loader2Icon, {
                                    className: "animate-spin",
                                    "aria-hidden": true
                                })
                            }),
                            thinkingPhases && thinkingPhases.length > 0 ? /*#__PURE__*/ _jsx(AiThinkingIndicator, {
                                phases: thinkingPhases,
                                variant: "inline"
                            }) : summaryLabel(runs.length)
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(MarkerIcon, {
                                variant: "ai",
                                children: /*#__PURE__*/ _jsx(CheckIcon, {
                                    "aria-hidden": true
                                })
                            }),
                            summaryLabel(runs.length)
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ChevronDownIcon, {
                        className: "size-4 shrink-0 transition-transform group-open/tools:rotate-180",
                        "aria-hidden": true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-2 flex flex-col gap-2 pl-6",
                children: runs.map(({ part, index })=>/*#__PURE__*/ _jsx(AiChatPanelToolActivity, {
                        part: part,
                        toolLabels: toolLabels,
                        ...rejectedToolLabel ? {
                            rejectedToolLabel
                        } : {},
                        detail: renderToolDetail?.(part)
                    }, part.toolCallId ?? `tool-${index}`))
            })
        ]
    });
}
function AiChatPanelFiles({ parts }) {
    return /*#__PURE__*/ _jsx(AttachmentGroup, {
        children: parts.map((part, index)=>/*#__PURE__*/ _jsxs(Attachment, {
                size: "sm",
                children: [
                    /*#__PURE__*/ _jsx(AttachmentMedia, {
                        children: /*#__PURE__*/ _jsx(ChatAttachmentIcon, {
                            mediaType: part.mediaType
                        })
                    }),
                    /*#__PURE__*/ _jsx(AttachmentContent, {
                        children: /*#__PURE__*/ _jsx(AttachmentTitle, {
                            children: part.filename
                        })
                    })
                ]
            }, `${part.filename ?? "file"}-${index}`))
    });
}
/**
 * Memoised because `renderText` parses markdown DURING render: without it every
 * settled turn re-parses on each streamed chunk of the turn below it. The memo
 * only holds while the host keeps its render props and its `assistantIcon`
 * referentially stable — hence `EMPTY_TOOL_LABELS` rather than a `{}` default.
 */ const AiChatPanelTurn = /*#__PURE__*/ React.memo(function AiChatPanelTurn({ message, assistantIcon, toolLabels, rejectedToolLabel, renderText, renderToolDetail, renderRichPart, renderMessageFooter, toolActivitySummaryLabel, formatMessageTimestamp, thinkingPhases }) {
    const isUser = message.role === "user";
    const files = message.parts.filter(isFilePart);
    const createdAt = message.metadata?.createdAt;
    const renderPart = (part, index)=>{
        if (isTextPart(part)) {
            if (!hasVisibleContent(part)) return null;
            return /*#__PURE__*/ _jsx(Bubble, {
                variant: isUser ? "accent" : "muted",
                align: isUser ? "end" : "start",
                children: /*#__PURE__*/ _jsx(BubbleContent, {
                    children: isUser ? renderTextAsPlainText(part.text) : renderText(part.text)
                })
            }, `text-${index}`);
        }
        if (isToolPart(part)) {
            return /*#__PURE__*/ _jsx(AiChatPanelToolActivity, {
                part: part,
                toolLabels: toolLabels,
                ...rejectedToolLabel ? {
                    rejectedToolLabel
                } : {},
                detail: renderToolDetail?.(part)
            }, part.toolCallId ?? `tool-${index}`);
        }
        if (isRichPart(part)) {
            return /*#__PURE__*/ _jsx(React.Fragment, {
                children: renderRichPart?.(part)
            }, part.toolCallId ?? `${part.type}-${index}`);
        }
        return null;
    };
    return /*#__PURE__*/ _jsxs(Message, {
        align: isUser ? "end" : "start",
        children: [
            !isUser && assistantIcon && /*#__PURE__*/ _jsx(MessageAvatar, {
                variant: "icon",
                children: assistantIcon
            }),
            /*#__PURE__*/ _jsxs(MessageContent, {
                // The mark belongs to the TURN, and its PRESENCE — not the role — is
                // what puts it there: a deterministic assistant turn, and one written
                // before the mark existed, carry none and stay unmarked.
                "data-ai-generated": !isUser && message.metadata?.aiProvenance ? "true" : undefined,
                children: [
                    files.length > 0 && /*#__PURE__*/ _jsx(AiChatPanelFiles, {
                        parts: files
                    }),
                    toolActivitySummaryLabel ? toTurnSegments(message.parts).map((segment)=>segment.kind === "tools" ? /*#__PURE__*/ _jsx(AiChatPanelToolGroup, {
                            runs: segment.runs,
                            summaryLabel: toolActivitySummaryLabel,
                            toolLabels: toolLabels,
                            ...thinkingPhases ? {
                                thinkingPhases
                            } : {},
                            ...rejectedToolLabel ? {
                                rejectedToolLabel
                            } : {},
                            ...renderToolDetail ? {
                                renderToolDetail
                            } : {}
                        }, segment.runs[0]?.part.toolCallId ?? `tools-${segment.runs[0]?.index}`) : renderPart(segment.part, segment.index)) : message.parts.map(renderPart),
                    !isUser && renderMessageFooter?.(message),
                    formatMessageTimestamp && // In flow with reserved height, not absolutely positioned: the
                    // scroller items paint-contain (`content-visibility: auto`), so
                    // anything drawn outside the turn's box is clipped away. The line is
                    // reserved even before `createdAt` arrives (a streaming assistant
                    // turn gets it on finish), so neither the reveal nor the arrival
                    // shifts the transcript under the reader.
                    /*#__PURE__*/ _jsx("span", {
                        "data-slot": "message-timestamp",
                        className: "pointer-events-none h-4 text-xs leading-4 text-muted-foreground opacity-0 transition-opacity group-focus-within/message:opacity-100 group-hover/message:opacity-100",
                        children: createdAt && /*#__PURE__*/ _jsx("time", {
                            dateTime: createdAt,
                            children: formatMessageTimestamp(createdAt)
                        })
                    })
                ]
            })
        ]
    });
});
function AiChatPanelSkeleton() {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "ai-chat-panel-skeleton",
        className: "flex min-h-0 flex-1 flex-col gap-6 px-4 py-6",
        children: [
            0,
            1,
            2
        ].map((row)=>/*#__PURE__*/ _jsx("div", {
                className: cn("h-4 animate-pulse rounded bg-muted", row % 2 === 0 ? "w-2/3 self-end" : "w-3/4")
            }, row))
    });
}
function renderTextAsPlainText(text) {
    return /*#__PURE__*/ _jsx("span", {
        className: "whitespace-pre-wrap",
        children: text
    });
}
export function AiChatPanel({ messages, header, composer, emptyState, assistantIcon, isLoading = false, isHistoryLoading = false, thinkingPhases, toolLabels = EMPTY_TOOL_LABELS, rejectedToolLabel, renderToolDetail, toolActivitySummaryLabel, formatMessageTimestamp, renderText = renderTextAsPlainText, renderRichPart, renderMessageFooter, composingAttachments, onFilesDropped, transcriptLabel = "Chat transcript", dropHintLabel, className }) {
    const { isDraggingOver, dropHandlers } = useFileDrop(onFilesDropped);
    const visibleMessages = messages.filter(hasRenderablePart);
    const showThinking = isLoading && awaitsFirstToken(messages);
    const showEmptyState = !isHistoryLoading && visibleMessages.length === 0 && !showThinking && !!emptyState;
    return /*#__PURE__*/ _jsxs("div", {
        "data-slot": "ai-chat-panel",
        className: cn("relative flex min-h-0 flex-1 flex-col bg-background", className),
        ...dropHandlers,
        children: [
            header,
            isHistoryLoading ? /*#__PURE__*/ _jsx(AiChatPanelSkeleton, {}) : showEmptyState ? // The COLUMN scrolls, not the empty state itself: a hero taller than
            // the panel then grows a scrollbar at the panel's edge instead of a
            // stray bar beside the centered content. `m-auto` centers what fits
            // and top-aligns what overflows — `justify-center` would clip the top.
            /*#__PURE__*/ _jsx("div", {
                "data-slot": "ai-chat-panel-empty",
                className: "flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-4 py-6",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "m-auto flex w-full flex-col items-center",
                    children: emptyState
                })
            }) : /*#__PURE__*/ _jsx(MessageScrollerProvider, {
                autoScroll: true,
                defaultScrollPosition: "end",
                scrollEdgeThreshold: SCROLL_EDGE_THRESHOLD,
                children: /*#__PURE__*/ _jsxs(MessageScroller, {
                    children: [
                        /*#__PURE__*/ _jsx(MessageScrollerViewport, {
                            preserveScrollOnPrepend: true,
                            "aria-label": transcriptLabel,
                            children: /*#__PURE__*/ _jsxs(MessageScrollerContent, {
                                className: cn(CHAT_COLUMN_MEASURE, "gap-6 px-4 py-6"),
                                children: [
                                    visibleMessages.map((message)=>/*#__PURE__*/ _jsx(MessageScrollerItem, {
                                            messageId: message.id,
                                            children: /*#__PURE__*/ _jsx(AiChatPanelTurn, {
                                                message: message,
                                                assistantIcon: assistantIcon,
                                                toolLabels: toolLabels,
                                                ...rejectedToolLabel ? {
                                                    rejectedToolLabel
                                                } : {},
                                                ...toolActivitySummaryLabel ? {
                                                    toolActivitySummaryLabel
                                                } : {},
                                                ...formatMessageTimestamp ? {
                                                    formatMessageTimestamp
                                                } : {},
                                                ...thinkingPhases ? {
                                                    thinkingPhases
                                                } : {},
                                                renderText: renderText,
                                                renderToolDetail: renderToolDetail,
                                                renderRichPart: renderRichPart,
                                                renderMessageFooter: renderMessageFooter
                                            })
                                        }, message.id)),
                                    showThinking && thinkingPhases && thinkingPhases.length > 0 && /*#__PURE__*/ _jsx(MessageScrollerItem, {
                                        messageId: THINKING_INDICATOR_ID,
                                        children: /*#__PURE__*/ _jsxs(Marker, {
                                            className: "items-start",
                                            children: [
                                                assistantIcon && /*#__PURE__*/ _jsx(MessageAvatar, {
                                                    variant: "icon",
                                                    children: assistantIcon
                                                }),
                                                /*#__PURE__*/ _jsx(AiThinkingIndicator, {
                                                    phases: thinkingPhases,
                                                    variant: "inline",
                                                    className: "py-1"
                                                })
                                            ]
                                        })
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(MessageScrollerButton, {})
                    ]
                })
            }),
            composingAttachments,
            composer,
            isDraggingOver && /*#__PURE__*/ _jsx(AiChatDropHint, {
                label: dropHintLabel
            })
        ]
    });
}

//# sourceMappingURL=ai-chat-panel.js.map